import { Box, Button } from "@mui/material";
import { ThemeProvider } from "@mui/material/styles";
import { Text } from "@src/appV2/redesign/components/Text";
import { berlinRebrandTheme } from "@src/appV2/redesign/theme/theme";
import { loginWithEmailAndPassword } from "@src/mobile/Login/loginWithEmailAndPassword";
import { sendLoginEmail } from "@src/mobile/Login/sendLoginEmail";
import { type ReactElement, useCallback, useState } from "react";

import clipboardWordmark from "../../../../public/assets/clipboard-wordmark-brown.png";
import loginIllustration from "./assets/login-illustration.svg";
import { LoginForm } from "./LoginForm";
import { LoginWithEmailAndPasswordForm } from "./LoginWithEmailAndPasswordForm";

type LoginMode = "magicLink" | "password";

export function Login(): ReactElement {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [loginMode, setLoginMode] = useState<LoginMode>("magicLink");

  const handleMagicLinkSubmit = useCallback(async () => {
    if (!email) {
      return;
    }

    setLoading(true);

    const success = await sendLoginEmail({
      email,
      requestedURL: localStorage.getItem("redirectURL") ?? undefined,
    });

    if (success) {
      setEmailSent(true);
    }

    setLoading(false);
  }, [email]);

  const handlePasswordSubmit = useCallback(async () => {
    if (!email || !password) {
      return;
    }

    setLoading(true);

    const success = await loginWithEmailAndPassword({ email, password });

    if (success) {
      setLoading(false);
      return;
    }

    setLoading(false);
  }, [email, password]);

  return (
    <ThemeProvider theme={berlinRebrandTheme}>
      <Box
        sx={{
          position: "fixed",
          inset: 0,
          display: "flex",
          flexDirection: "column",
          backgroundColor: "background.baseDark",
          overflow: "auto",
          paddingX: 5,
          paddingTop: "max(24px, env(safe-area-inset-top))",
          paddingBottom: "max(24px, env(safe-area-inset-bottom))",
          justifyContent: "center",
          alignItems: "center",
          zIndex: 1,
        }}
      >
        {/* Wordmark */}
        <img
          src={clipboardWordmark}
          alt="Clipboard"
          style={{ height: 70, width: "auto", marginBottom: 40 }}
        />

        {/* Illustration - hidden on verification sent state */}
        {!(loginMode === "magicLink" && emailSent) && (
          <img
            src={loginIllustration}
            alt="Two people shaking hands"
            style={{ height: 170, width: "auto", marginBottom: 40 }}
          />
        )}

        {/* Form */}
        <Box sx={{ width: "100%", maxWidth: 400 }}>
          {loginMode === "magicLink" && !emailSent && (
            <LoginForm
              email={email}
              loading={loading}
              setEmail={setEmail}
              onSubmit={handleMagicLinkSubmit}
              onSwitchToPassword={() => {
                setLoginMode("password");
              }}
            />
          )}

          {loginMode === "magicLink" && emailSent && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 6,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Text sx={{ color: "primary.main", fontSize: "1.25rem" }}>✓</Text>
                <Text bold variant="h5" sx={{ color: "base.base" }}>
                  Verification link sent
                </Text>
              </Box>
              <Text bold variant="body1" sx={{ color: "base.muted", textAlign: "center" }}>
                We have sent an email with a login link to {email}.
              </Text>
              <Button
                variant="text"
                sx={{
                  textTransform: "none",
                  fontSize: "0.875rem",
                  color: "primary.main",
                }}
                onClick={() => {
                  setEmailSent(false);
                }}
              >
                Send another email
              </Button>
            </Box>
          )}

          {loginMode === "password" && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "stretch",
                width: "100%",
              }}
            >
              <LoginWithEmailAndPasswordForm
                email={email}
                password={password}
                loading={loading}
                setEmail={setEmail}
                setPassword={setPassword}
                onSubmit={handlePasswordSubmit}
              />
              {/* Divider */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  alignSelf: "stretch",
                  gap: 5,
                  marginTop: 4,
                }}
              >
                <Box
                  sx={{
                    flex: 1,
                    height: "1px",
                    backgroundColor: "border.base",
                  }}
                />
                <Text variant="body2" sx={{ color: "base.inactive" }}>
                  or
                </Text>
                <Box
                  sx={{
                    flex: 1,
                    height: "1px",
                    backgroundColor: "border.base",
                  }}
                />
              </Box>

              <Button
                variant="text"
                disabled={loading}
                sx={{
                  textTransform: "none",
                  fontSize: "0.875rem",
                  color: "primary.main",
                  marginTop: 4,
                  alignSelf: "center",
                }}
                onClick={() => {
                  setLoginMode("magicLink");
                }}
              >
                Sign in with magic link
              </Button>
            </Box>
          )}
        </Box>
      </Box>
    </ThemeProvider>
  );
}
